import * as React from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Icon, Skeleton, Tabs, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import { PanesPad } from 'modules/core/components/PanesPad'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import AnalyticsActions from 'modules/reviews/components/Analytics/AnalyticsActions'
import CycleSelector from 'modules/reviews/components/Analytics/CycleSelector'
import Filters from 'modules/reviews/components/Analytics/Filters'
import filterFields from 'modules/reviews/components/Analytics/Filters/filterFields'
import RawResults from 'modules/reviews/components/Analytics/RawResults'
import ScoreResults from 'modules/reviews/components/Analytics/ScoreResults'
import buildTabs, { TABS_IDS } from 'modules/reviews/components/Analytics/tabs'
import ReviewShowWrapper from 'modules/reviews/components/Show/ReviewShowWrapper'
import useAnalyticsMultiFilters, {
  AnalyticsFilterForm,
} from 'modules/reviews/hooks/useAnalyticsMultiFilters'
import { useReviewShowContext } from 'modules/reviews/pages/performance/Show/hooks'
import type Review from 'modules/reviews/store/models/v2/Review'

import { ReviewAnalyticsProvider, useAnalytics } from './analyticsContext'

const ReviewAnalyticsPage = observer(() => {
  const { review: reviewModel } = useReviewShowContext()
  const [tabs, setTabs] = React.useState(buildTabs())

  const { loading, analyticResults, cycle, setCycle, setReviewAnalyticsFilter } = useAnalytics()

  const {
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
    localFilter,
  } = useAnalyticsMultiFilters(setReviewAnalyticsFilter)
  const [fields, setFields] = React.useState<readonly MultiFilterOption[]>([])

  const { loading: loadingFilters } = useAsyncData(async () => {
    const fields = await filterFields()

    setFields(fields)
  }, [])

  return (
    <PanesPad>
      <Box gap="s16" paddingBottom="s32" alignItems="flexStart">
        <Box>
          <Icon.Large icon="surveys" />
        </Box>
        <Text.Header2>{i18n.t('surveys.analytics.title')}</Text.Header2>
        <Text.Normal>{i18n.t('surveys.review.analytics.description')}</Text.Normal>
        <Box flexDirection="row" gap="s16">
          {loadingFilters && <Skeleton width="s96" height="s32" />}
          {!loadingFilters && (
            <Filters
              label={i18n.t('filters.multi_filters.title')}
              fields={fields}
              filters={filters}
              logicOperator={logicOperator}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onApplyFilters={(isActive, defaultFilter) => {
                onApplyFilters(isActive, defaultFilter)
                setReviewAnalyticsFilter(
                  new AnalyticsFilterForm(filters, logicOperator.values, defaultFilter, isActive)
                )
              }}
              onCancel={onCancel}
              filterKey={localFilter}
            />
          )}
          {reviewModel.hasSchedule && !loading && (
            <CycleSelector analyticResults={analyticResults} cycle={cycle} setCycle={setCycle} />
          )}
          <AnalyticsActions form={reviewModel} />
        </Box>
      </Box>
      {loading && <Loading />}
      {!loading && (
        <Tabs
          currentId={tabs.find((tab) => tab.active)?.id ?? ''}
          onTabClicked={(tabId) => {
            setTabs(
              tabs.map((tab) => {
                if (tab.id === tabId) {
                  return {
                    ...tab,
                    active: true,
                  }
                }
                return {
                  ...tab,
                  active: false,
                }
              })
            )
          }}
        >
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.id} id={tab.id} label={tab.label}>
              <Box paddingTop="s40">
                {tab.id === TABS_IDS.summary && <ScoreResults form={reviewModel} />}
                {tab.id === TABS_IDS.raw && <RawResults formName={reviewModel.attributes.name} />}
              </Box>
            </Tabs.Tab>
          ))}
        </Tabs>
      )}
    </PanesPad>
  )
})

const WrappedAnalytics = () => {
  const { review } = useOutletContext<{
    review: Review
  }>()

  return (
    <ReviewAnalyticsProvider form={review}>
      <ReviewShowWrapper reviewId={Number(review.attributes.id)}>
        <ReviewAnalyticsPage />
      </ReviewShowWrapper>
    </ReviewAnalyticsProvider>
  )
}

export default WrappedAnalytics
